/**
 * ProductionsBoard — unified "My Productions" workspace. One list of every
 * multi-part project with a computed status pipeline:
 *   Draft → Ready to Stitch → Stitching → Stitched → Published → Archived
 * Replaces the old Project Queue + Recent Stitches panels.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  LayoutList, Loader2, RefreshCw, Pencil, Check, X, Archive,
  ArchiveRestore, Trash2, FolderOpen, Sparkles, ExternalLink, Film,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_META = {
  draft: { label: "Draft", cls: "border-white/20 text-[color:var(--muted)]" },
  ready: { label: "Ready to stitch", cls: "border-amber-400/50 text-amber-300" },
  stitching: { label: "Stitching…", cls: "border-sky-400/50 text-sky-300 animate-pulse" },
  stitched: { label: "Stitched", cls: "border-emerald-400/50 text-emerald-300" },
  published: { label: "Published", cls: "border-[color:var(--accent)]/60 text-[color:var(--accent)]" },
  archived: { label: "Archived", cls: "border-white/15 text-[color:var(--muted)] opacity-70" },
};

const TABS = [
  { key: "active", label: "Active" },
  { key: "published", label: "Published" },
  { key: "archived", label: "Archived" },
];

function fmtWhen(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString(undefined, {
    day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
  });
}

function fmtDur(secs) {
  if (!secs) return null;
  const m = Math.floor(secs / 60);
  const s = Math.round(secs % 60);
  return `${m}m ${String(s).padStart(2, "0")}s`;
}

export default function ProductionsBoard({ currentSessionId, onOpenProject, onOpenResult }) {
  const [projects, setProjects] = useState(null);
  const [disk, setDisk] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("active");
  const [renaming, setRenaming] = useState(null); // session_id
  const [renameVal, setRenameVal] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null); // session_id
  const [busyId, setBusyId] = useState(null);
  const pollRef = useRef(null);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await axios.get(`${API}/multi-part/projects`);
      setProjects(data.projects || []);
      setDisk(data.disk || null);
    } catch {
      if (!silent) toast.error("Could not load productions");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep polling while anything is stitching.
  useEffect(() => {
    const anyStitching = (projects || []).some((p) => p.status === "stitching");
    clearInterval(pollRef.current);
    if (anyStitching) pollRef.current = setInterval(() => load(true), 10_000);
    return () => clearInterval(pollRef.current);
  }, [projects]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveRename = async (sid) => {
    const name = renameVal.trim();
    setRenaming(null);
    if (!name) return;
    try {
      const fd = new FormData();
      fd.append("name", name);
      await axios.post(`${API}/multi-part/session/${sid}/rename`, fd);
      toast.success("Project renamed");
      load(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rename failed");
    }
  };

  const setArchived = async (sid, archived) => {
    setBusyId(sid);
    try {
      const fd = new FormData();
      fd.append("archived", archived ? "true" : "false");
      await axios.post(`${API}/multi-part/session/${sid}/archive`, fd);
      toast.success(archived ? "Project archived" : "Project restored");
      load(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update project");
    } finally {
      setBusyId(null);
    }
  };

  const doDelete = async (sid) => {
    setConfirmDelete(null);
    setBusyId(sid);
    try {
      await axios.delete(`${API}/multi-part/session/${sid}`);
      toast.success("Project deleted");
      setProjects((prev) => (prev || []).filter((p) => p.session_id !== sid));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setBusyId(null);
    }
  };

  const list = projects || [];
  const grouped = {
    active: list.filter((p) => ["draft", "ready", "stitching", "stitched"].includes(p.status)),
    published: list.filter((p) => p.status === "published"),
    archived: list.filter((p) => p.status === "archived"),
  };
  const visible = grouped[tab] || [];

  return (
    <div className="panel p-6 md:p-8" data-testid="productions-board">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="label-eyebrow mb-1 flex items-center gap-2">
            <LayoutList className="w-3.5 h-3.5" /> My productions
          </div>
          <div className="text-xs text-[color:var(--muted)] leading-relaxed max-w-xl">
            Every project in one place. Published work auto-moves out of your
            active workspace; use Archive for anything else you're done with.
          </div>
        </div>
        <div className="shrink-0 flex items-center gap-3">
          {disk && (
            <span
              className={`text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 rounded-full border inline-flex items-center gap-1.5 ${
                disk.free_gb < 2
                  ? "border-red-400/60 text-red-300"
                  : disk.free_gb < 4
                    ? "border-amber-400/50 text-amber-300"
                    : "border-emerald-400/40 text-emerald-300"
              }`}
              title={`Server workspace: ${disk.free_gb} GB free of ${disk.total_gb} GB. Stitching needs roughly 2.5× the size of your parts — caches are cleared automatically when space runs low.`}
              data-testid="productions-disk-health"
            >
              <span className={`w-1.5 h-1.5 rounded-full ${
                disk.free_gb < 2 ? "bg-red-400" : disk.free_gb < 4 ? "bg-amber-400" : "bg-emerald-400"
              }`} />
              {disk.free_gb} GB free
            </span>
          )}
          <button
            onClick={() => load()}
            className="text-[color:var(--muted)] hover:text-[color:var(--accent)] transition-colors"
            title="Refresh"
            data-testid="productions-refresh"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4" data-testid="productions-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`text-[11px] uppercase tracking-[0.15em] px-3 py-1.5 rounded-full border transition-colors ${
              tab === t.key
                ? "border-[color:var(--accent)] text-[color:var(--accent)] bg-[color:var(--accent)]/10"
                : "border-white/15 text-[color:var(--muted)] hover:border-white/40"
            }`}
            data-testid={`productions-tab-${t.key}`}
          >
            {t.label} ({grouped[t.key].length})
          </button>
        ))}
      </div>

      {projects === null ? (
        <div className="text-xs text-[color:var(--muted)] flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading productions…
        </div>
      ) : visible.length === 0 ? (
        <div className="text-xs text-[color:var(--muted)]" data-testid="productions-empty">
          {tab === "active"
            ? "No active productions — split a script or upload parts to start one."
            : tab === "published"
              ? "Nothing published from the multi-part builder yet."
              : "Nothing archived."}
        </div>
      ) : (
        <div className="space-y-2">
          {visible.map((p) => {
            const meta = STATUS_META[p.status] || STATUS_META.draft;
            const j = p.latest_stitch;
            const isCurrent = p.session_id === currentSessionId;
            return (
              <div
                key={p.session_id}
                className={`rounded-lg border p-3 ${isCurrent ? "border-[color:var(--accent)]/40 bg-white/[0.03]" : "border-white/[0.08]"}`}
                data-testid={`production-row-${p.session_id}`}
              >
                <div className="flex items-center gap-3 flex-wrap">
                  <span
                    className={`shrink-0 text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full border ${meta.cls}`}
                    data-testid={`production-status-${p.session_id}`}
                  >
                    {meta.label}
                  </span>
                  <div className="flex-1 min-w-0">
                    {renaming === p.session_id ? (
                      <div className="flex items-center gap-1.5">
                        <input
                          value={renameVal}
                          onChange={(e) => setRenameVal(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") saveRename(p.session_id);
                            if (e.key === "Escape") setRenaming(null);
                          }}
                          className="flex-1 min-w-0 bg-transparent border border-[color:var(--accent)]/50 rounded px-2 py-1 text-sm outline-none"
                          autoFocus
                          data-testid={`production-rename-input-${p.session_id}`}
                        />
                        <button onClick={() => saveRename(p.session_id)} className="text-emerald-400" data-testid={`production-rename-save-${p.session_id}`}>
                          <Check className="w-4 h-4" />
                        </button>
                        <button onClick={() => setRenaming(null)} className="text-[color:var(--muted)]">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className="text-sm truncate" data-testid={`production-name-${p.session_id}`}>
                          {p.name}{isCurrent && <span className="text-[color:var(--accent)] text-[10px] ml-1.5 uppercase tracking-wider">· open now</span>}
                        </span>
                        <button
                          onClick={() => { setRenaming(p.session_id); setRenameVal(p.has_custom_name ? p.name : ""); }}
                          className="shrink-0 text-[color:var(--muted)] hover:text-[color:var(--accent)]"
                          title="Rename this project"
                          data-testid={`production-rename-${p.session_id}`}
                        >
                          <Pencil className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                    <div className="text-[10px] text-[color:var(--muted)] mt-0.5 flex flex-wrap gap-x-2">
                      <span>{fmtWhen(p.updated_at)}</span>
                      <span>{p.parts_uploaded}{p.chunk_count ? `/${p.chunk_count}` : ""} parts</span>
                      {j?.status === "done" && fmtDur(j.duration_seconds) && (
                        <span className="text-emerald-400">{fmtDur(j.duration_seconds)}</span>
                      )}
                      {p.status === "stitching" && j && (
                        <span className="text-sky-300">{j.stage} · {j.progress || 0}%</span>
                      )}
                      {j?.status === "failed" && j.error && (
                        <span className="text-rose-300 truncate">{j.error}</span>
                      )}
                    </div>
                  </div>

                  <div className="shrink-0 flex items-center gap-1.5 flex-wrap">
                    {p.status === "published" && j?.published_video_slug && (
                      <a
                        href={`/s/${encodeURIComponent(j.published_video_slug)}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 rounded-md border border-white/15 hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] inline-flex items-center gap-1"
                        data-testid={`production-view-${p.session_id}`}
                      >
                        <ExternalLink className="w-3 h-3" /> View story
                      </a>
                    )}
                    {p.status === "stitched" && j?.output_url && (
                      <>
                        <a
                          href={`${BACKEND_URL}${j.output_url}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 rounded-md border border-white/15 hover:border-[color:var(--accent)] hover:text-[color:var(--accent)]"
                          data-testid={`production-preview-${p.session_id}`}
                        >
                          Preview
                        </a>
                        <button
                          onClick={() => onOpenResult?.(j)}
                          className="text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 rounded-md border border-[color:var(--accent)]/50 text-[color:var(--accent)] hover:bg-[color:var(--accent)]/10 inline-flex items-center gap-1"
                          data-testid={`production-publish-${p.session_id}`}
                        >
                          <Sparkles className="w-3 h-3" /> Publish
                        </button>
                      </>
                    )}
                    {["draft", "ready", "stitching", "stitched"].includes(p.status) && (
                      <button
                        onClick={() => onOpenProject?.(p.session_id)}
                        className="text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 rounded-md border border-white/15 hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] inline-flex items-center gap-1"
                        data-testid={`production-open-${p.session_id}`}
                      >
                        <FolderOpen className="w-3 h-3" /> Open
                      </button>
                    )}
                    {p.status !== "archived" ? (
                      <button
                        onClick={() => setArchived(p.session_id, true)}
                        disabled={busyId === p.session_id}
                        className="text-[color:var(--muted)] hover:text-amber-300 p-1"
                        title="Archive — hide from the active workspace (nothing is deleted)"
                        data-testid={`production-archive-${p.session_id}`}
                      >
                        <Archive className="w-3.5 h-3.5" />
                      </button>
                    ) : (
                      <button
                        onClick={() => setArchived(p.session_id, false)}
                        disabled={busyId === p.session_id}
                        className="text-[color:var(--muted)] hover:text-emerald-300 p-1"
                        title="Restore to the active workspace"
                        data-testid={`production-unarchive-${p.session_id}`}
                      >
                        <ArchiveRestore className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {confirmDelete === p.session_id ? (
                      <button
                        onClick={() => doDelete(p.session_id)}
                        className="text-[10px] uppercase tracking-[0.1em] px-2 py-1 rounded-md border border-red-400 text-red-300"
                        data-testid={`production-delete-confirm-${p.session_id}`}
                      >
                        Sure?
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          setConfirmDelete(p.session_id);
                          setTimeout(() => setConfirmDelete((v) => (v === p.session_id ? null : v)), 4000);
                        }}
                        disabled={busyId === p.session_id}
                        className="text-[color:var(--muted)] hover:text-red-400 p-1"
                        title="Delete project — removes its script and uploaded parts (published videos are NOT affected)"
                        data-testid={`production-delete-${p.session_id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
